import { SortingState } from "@tanstack/react-table";
import TableContainer from "Common/TableContainer";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Card, Col, Container, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import { createSelector } from "reselect";

import LoaderErrorWrapper from "Components/LoaderErrorWrapper/LoaderErrorWrapper";
import { TemplateActionMenu } from "Components/MenuActionButton/TemplateActionMenu";
import SearchBox from "Components/SearchBox/SearchBox";
import { app_modules } from "helpers/apps_modules_list";
import { formatDate } from "helpers/functions/formatDate";
import { toast } from "react-toastify";
import { fetchLatestPermissions } from "slices/auth/login/thunk";
import {
  deleteTemplateById,
  exportTemplateCSV,
  fetchTemplate,
  updateTemplateStatus,
} from "slices/template/thunk";
import Swal from "sweetalert2";

const Template = () => {
  document.title = "Template List";
  const dispatch = useDispatch<any>();

  const selectTemplate = createSelector(
    (state: any) => state.TemplateReducer || [],
    (template) => ({
      items: template.templateList,
      rowCount: template.rowCount,
      loading: template.loading,
      error: template.error,
    })
  );

  const { items, rowCount, loading, error } = useSelector(selectTemplate);

  const user = useSelector((state: any) => state.Login.user);
  const user_permissions = user?.permissions || [];
  const master_permissions = user_permissions?.find(
    (item: any) => item?.apps_name === app_modules?.template?.app_name
  );
  const template_permissions = master_permissions?.modules_permissions?.find(
    (item: any) =>
      item?.modules_name === app_modules?.template?.modules?.template
  );

  //*// UserType with refreashed user(in permission fetch)
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const isAdmin = authUser?.user_type === "Admin";

  //*// For redirecting back to this page and get the same paginated pageIndex
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = parseInt(searchParams.get("page_template") || "0");

  const [pageIndex, setPageIndex] = useState(pageParam);

  // Save current page to sessionStorage when component mounts
  useEffect(() => {
    if (pageParam > 0) {
      sessionStorage.setItem("templatePage", pageParam.toString());
    } else {
      // Clear sessionStorage when navigating from sidebar (page 0)
      sessionStorage.removeItem("templatePage");
    }
  }, [pageParam]);
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<any>();
  // const [filterValue, setFilterValue] = useState<any[]>([]);
  const [colValue, setColValue] = useState<any>();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  useEffect(() => {
    dispatch(
      fetchTemplate({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: colValue,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sorting,
    search,
    // colValue,
    sortField,
    sortDirection,
  ]);

  useEffect(() => {
    dispatch(
      fetchTemplate({
        page: 1, // Always use page 1 when filters change
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: colValue,
      })
    );
  }, [colValue]);

  useEffect(() => {
    setData(items);
  }, [items]);

  // Sync pageIndex with URL parameters
  useEffect(() => {
    const currentPageParam = parseInt(searchParams.get("page_template") || "0");
    if (currentPageParam !== pageIndex) {
      setPageIndex(currentPageParam);
    }
  }, [searchParams, pageIndex]);

  //*// All pagination Issues(resolved) when redirecting from the edit or view page
  // Reset pagenumber to 0 when not first render
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    handlePageChange(0);
  }, [sorting, pageSize, columnFilters, search]);

  const handlePageChange = (newPage: number) => {
    setPageIndex(newPage);
    setSearchParams({ page_template: newPage.toString() });
    // Save current page to sessionStorage for back navigation
    sessionStorage.setItem("templatePage", newPage.toString());
  };
  // Debounced search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);
  //*//

  //*// Fetch Permissions
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        await dispatch(fetchLatestPermissions()).unwrap();
      } catch (error) {
        console.error("Failed to fetch latest permissions", error);
        // toast.error("Failed to fetch latest permissions");
      }
    };
    fetchPermissions();
  }, []);

  const handleStatusToggle = useCallback(
    async (temp: any) => {
      const id = temp.template_id;

      try {
        await dispatch(updateTemplateStatus(id)).unwrap();
        toast.success(
          `Template ${temp.status ? "deactivated" : "activated"} successfully`
        );
        dispatch(
          fetchTemplate({
            page: pageIndex + 1,
            pageSize,
            sort: sortField,
            order: sortField ? sortDirection : "",
            search,
            filters: colValue,
          })
        );
      } catch (err) {
        console.error(err);
        toast.error("Failed to update status");
      }
    },
    [dispatch, pageIndex, pageSize, sortField, sortDirection, search, colValue]
  );

  //*// updated Export
  const handleExport = async () => {
    try {
      const { blob, filename } = await exportTemplateCSV({
        search,
        filters: colValue,
      });

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Export failed", error);
    }
  };

  const handleDelete = useCallback(
    async (id: any) => {
      if (!isAdmin) {
        toast.error("Only administrators can delete templates");
        return;
      }

      const result = await Swal.fire({
        title: "Are you sure?",
        text: "Are you sure you want to delete this template?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete it!",
        width: "350px",
        customClass: { popup: "swal2-sm-popup" },
      });

      if (!result.isConfirmed) return;

      try {
        await dispatch(deleteTemplateById(id)).unwrap();
        toast.success("Template deleted successfully!");

        if (data.length === 1 && pageIndex > 0) {
          handlePageChange(pageIndex - 1);
        } else {
          dispatch(
            fetchTemplate({
              page: pageIndex + 1,
              pageSize,
              sort: sortField,
              order: sortField ? sortDirection : "",
              search,
              filters: colValue,
            })
          );
        }
      } catch (err) {
        toast.error("Failed to delete template");
        console.error(err);
      }
    },
    [
      dispatch,
      isAdmin,
      data.length,
      pageIndex,
      pageSize,
      sortField,
      sortDirection,
      search,
      colValue,
    ]
  );

  const DynamicAction = useMemo(() => {
    // if (template_permissions?.permissions?.Update) {
    return [
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <TemplateActionMenu
            row={row}
            handleDelete={handleDelete}
            canChange={!!template_permissions?.permissions?.Update}
            canDelete={isAdmin}
          />
        ),
      },
    ];
    // }
    return [];
  }, [template_permissions, handleDelete]);

  const columns = useMemo(
    () => [
      // {
      //   header: "Sr. No.",
      //   id: "sr_no", // important: since there's no accessorKey
      //   enableSorting: false,
      //   enableColumnFilter: false,
      //   cell: ({ row }: { row: any }) => {
      //     return row.index + 1 + pageIndex * pageSize;
      //   },
      // },
      {
        header: "Template Name",
        accessorKey: "template_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "/api/templates/column-filter-dropdown/?column_filter=template_name",
        cell: ({ row }: { row: any }) => (
          <div
            style={{
              whiteSpace: "normal", // allow line breaks
              wordWrap: "break-word", // break long words
              maxWidth: "200px", // set a maximum width
              overflow: "hidden", // prevent overflow
              textOverflow: "ellipsis", // optional: show "..." if truncated
            }}
          >
            {row.original.template_name || "-"}
          </div>
        ),
      },
      {
        header: "Description",
        accessorKey: "description",
        enableColumnFilter: false,
        enableSorting: false,
        // filterType: "dropdown" as const,
        // filterFetchUrl:
        //   "/api/item/column-filter-dropdown/?column_filter=item_name",
        cell: ({ row }: { row: any }) => (
          <div
            style={{
              whiteSpace: "normal", // allow line breaks
              wordWrap: "break-word", // break long words
              maxWidth: "200px", // set a maximum width
              overflow: "hidden", // prevent overflow
              textOverflow: "ellipsis", // optional: show "..." if truncated
            }}
          >
            {row.original.description || "-"}
          </div>
        ),
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const temp = row.original;

          return (
            <div
              style={{ fontSize: "1.5rem", cursor: "pointer" }}
              onClick={() => handleStatusToggle(temp)}
            >
              <i
                className={`bi ${
                  temp.status
                    ? "bi-toggle-on text-primary"
                    : "bi-toggle-off text-danger"
                }`}
              />
            </div>
          );
        },
      },

      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        filterType: "date" as const,
        enableSorting: true,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_name ?? "N/A";
          const date = formatDate(row.original.created_at);
          return `  ${name}  -  ${date}  `; // extra spaces for visual padding
        },
      },

      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        filterType: "date" as const,
        enableSorting: true,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_name ?? "N/A";
          const date = formatDate(row.original.updated_at);
          return `  ${name}  -  ${date}  `; // spaces before/after the name
        },
      },
      ...DynamicAction,
    ],
    [pageIndex, pageSize, handleStatusToggle, DynamicAction]
  );

  // console.log(template_permissions);
  return (
    <Fragment>
      <div className="page-content">
        <Container fluid>
          <Row id="invoiceList">
            <Col lg={12}>
              <Card>
                <Card.Header>
                  <Row className="align-items-center g-2">
                    <Col lg={3} className="me-auto">
                      <h6 className="card-title mb-0">Template</h6>
                    </Col>

                    {/* Search Input Box (reusable)*/}
                    <Col xl={2} md={3}>
                      <SearchBox
                        value={searchInput}
                        onChange={setSearchInput}
                      />
                    </Col>

                    <Col className="col-md-auto">
                      <div className="hstack gap-2">
                        {/* {template_permissions?.permissions?.View === true && (
                          <div
                            className="hstack gap-2 btn btn-light"
                            onClick={handleExport}
                            style={{ cursor: "pointer" }}
                          >
                            <i className="bi bi-box-arrow-up align-baseline me-1"></i>{" "}
                            Export
                          </div>
                        )} */}
                        {template_permissions?.permissions?.Create === true && (
                          <Link to="/add-template" className="btn btn-primary">
                            <i className="bi bi-plus-circle align-baseline me-1"></i>{" "}
                            Create Template
                          </Link>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Card.Header>
                <Card.Body className="mt-3">
                  <LoaderErrorWrapper loading={loading} error={error}>
                    <TableContainer
                      columns={columns}
                      data={data}
                      totalRecords={rowCount}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      // onPageChange={setPageIndex}
                      onPageChange={handlePageChange} // for persisted pagination across pages
                      onPageSizeChange={setPageSize}
                      columnFilters={columnFilters}
                      setColumnFilters={setColumnFilters}
                      colValue={setColValue}
                      sorting={sorting}
                      onSortingChange={setSorting}
                      manualSorting={true}
                      isBordered={false}
                      isPagination={true}
                      isGlobalFilter={false}
                      //filterOptions={filterOptions}
                      SearchPlaceholder="Search Items..."
                    />
                  </LoaderErrorWrapper>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </Fragment>
  );
};

export default Template;
